/**
 * Was eine Tabelle in einer Chat-Antwort braucht, rein und ohne DOM: ihre Zellen als Text, die
 * Mindestbreite je Spalte und die Ausgabe für Excel.
 */

type HastNode = {
    type: string;
    tagName?: string;
    value?: string;
    children?: HastNode[];
};

const textOf = (node: HastNode): string =>
    node.type === 'text'
        ? node.value ?? ''
        : node.tagName === 'br'
          ? '\n'
          : (node.children ?? []).map(textOf).join('');

/** Die Zeilen einer Tabelle (Kopf und Rumpf) als Text, aus dem hast-Knoten von react-markdown. */
export function tableRows(table: HastNode | undefined): string[][] {
    const rows: string[][] = [];
    const visit = (node: HastNode) => {
        if (node.tagName === 'tr') {
            rows.push(
                (node.children ?? [])
                    .filter(cell => cell.tagName === 'td' || cell.tagName === 'th')
                    .map(cell => textOf(cell).replace(/\s+/g, ' ').trim())
            );
            return;
        }
        (node.children ?? []).forEach(visit);
    };
    if (table) visit(table);
    return rows;
}

/**
 * Die Mindestbreite je Spalte in rem (8, 14 oder 20), nach ihrer längsten Zelle — `undefined`: so schmal wie ihr
 * Inhalt. Ohne sie quetscht der Browser jede Spalte auf die verfügbare Breite: Sechs Spalten in
 * 500 px wurden je 45 px schmal, ein „Spalte 1 Wert" brach Wort für Wort (gemessen 06.10.2026).
 * Mit ihr bleibt eine Tabelle, die passt, wie sie war, und eine, die nicht passt, rollt seitlich.
 */
export function columnMinWidths(rows: string[][]): Array<number | undefined> {
    const columns = Math.max(0, ...rows.map(row => row.length));
    return Array.from({ length: columns }, (_, column) => {
        const longest = Math.max(0, ...rows.map(row => (row[column] ?? '').length));
        // Eine Zelle mit einem ganzen Absatz bekäme sonst sechs, sieben Zeilen Höhe — und mit ihr
        // die ganze Zeile (157 Zeichen in 14rem: 169 px je Zeile, gemessen 06.10.2026).
        if (longest > 100) return 20;
        if (longest > 40) return 14;
        if (longest > 12) return 8;
        return undefined;
    });
}

const csvCell = (value: string): string => (/[";\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);

/**
 * CSV, wie ein deutsches Excel es per Doppelklick öffnet: Semikolon, CRLF, und vorn die BOM —
 * ohne sie liest Excel die Datei als Windows-1252, und aus „ä" wird „Ã¤".
 */
export function toCsv(rows: string[][]): string {
    return '﻿' + rows.map(row => row.map(csvCell).join(';')).join('\r\n') + '\r\n';
}

/** Tabulatorgetrennt — so fügt Excel aus der Zwischenablage in Zellen ein. */
export function toTsv(rows: string[][]): string {
    return rows.map(row => row.map(cell => cell.replace(/[\t\r\n]+/g, ' ')).join('\t')).join('\n');
}
